import React, { useRef, useState } from "react";
import {
  BackIcon,
  DarkModeIcon,
  LightModeIcon,
  PasswordIcon,
} from "../../components/iconSet/IconSet";

import { EyeIcon, EyeOffIcon } from "../../components/iconSet/NewIconSet";
import { ThemeTypes } from "../../context/ContextTypes";

import logoIcon from "../../images/icons/logo.png";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import AlertDialog from "../../components/popupComp/AlertDialog";
import axios from "axios";
import "./ResetForgotPasswordNew2.css";

const ResetForgotPasswordNew2 = () => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { theme, setTheme } = useTheme();

  const passwordRef = useRef<HTMLInputElement | null>(null);
  const confirmPasswordRef = useRef<HTMLInputElement | null>(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [alertDialogState, setAlertDialogState] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertDescription, setAlertDescription] = useState("");
  const [alertYesCallback, setAlertYesCallback] = useState<(() => void) | undefined>(undefined);
  const [alertOkCallback, setAlertOkCallback] = useState<(() => void) | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [error, setError] = useState<string>('')
        const [success, setSuccess] = useState<string>('')

  const showAlertDialog = (
    title: string,
    description: string,
    yesCallback?: () => void,
    okCallback?: () => void
  ) => {
    setAlertTitle(title);
    setAlertDescription(description);
    setAlertYesCallback(() => yesCallback);
    setAlertOkCallback(() => okCallback);
    setAlertDialogState(true);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const newPassword = passwordRef.current?.value.trim();
    const confirmPassword = confirmPasswordRef.current?.value.trim();

    if (!newPassword || !confirmPassword) {
      //showAlertDialog("Validation Error", "Please fill in both password fields.");
      setError("Please fill in both password fields.");
      setIsSubmitting(false);
      return;
    }

    if (newPassword.length < 8) {
      //showAlertDialog("Validation Error", "Password must be at least 8 characters.");
      setError("Password must be at least 8 characters.");
      setIsSubmitting(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      //showAlertDialog("Mismatch", "Passwords do not match.");
      setError("Passwords do not match.");
      setIsSubmitting(false);
      return;
    }

    if (!token) {
      //showAlertDialog("Error", "Missing or invalid reset token.");
      setError("Missing or invalid reset token.");
      setIsSubmitting(false);
      return;
    }

    try {
      await axios.put(
        `${BASE_URL}/jwt-auth/api/v1/reset-forgot-password?token=${token}`,
        { newPassword },
        { headers: { "Content-Type": "application/json" } }
      );

      setSuccess("Password reset successfully. You can now login with your new password.");
      navigate("/login");
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        error.response?.data ||
        "Failed to reset password. Please try again.";
      //showAlertDialog("❌ Error", message);
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleThemeChangeClick = (type: ThemeTypes) => {
    setTheme((prevState) => type);
  };

  return (
    <div className="reset-password-root">
      {/* Background Video */}
      <video autoPlay muted playsInline className="background-video" loop>
        <source src="/MV_FVM_Video.mp4" type="video/mp4" />
      </video>
      <div className="video-overlay" />

      {/* Main Content */}
      <div className="reset-password-container">
        <div className="reset-password-card">
          {/* Brand Header */}
          <div className="brand-area">
            <img src={logoIcon} alt="BCONX" className="brand-logo" />
            <h1>BCONX</h1>
            <p className="brand-subtitle">
              Backoffice System
            </p>
          </div>

          {/* Form Section */}
          <form onSubmit={handleResetPassword} className="reset-form">
            <div className="form-header">
              <h2>Create New Password</h2>
              <p className="form-description">
                Enter and confirm your new password below.
              </p>
            </div>

            {/* Password Field */}
            <div className="inputGroup">
              <label htmlFor="new-password">New Password</label>
              <div className="password-wrapper">
                <span className="input-icon">
                  <PasswordIcon color="rgba(255,255,255,0.6)" width="18px" />
                </span>
                <input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  ref={passwordRef}
                  required
                  autoComplete="new-password"
                  disabled={isSubmitting}
                  minLength={8}
                />
                <button
                  type="button"
                  className="toggle-visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  disabled={isSubmitting}
                >
                  {showPassword ? (
                    <EyeOffIcon color="rgba(255,255,255,0.7)" width="18px" />
                  ) : (
                    <EyeIcon color="rgba(255,255,255,0.7)" width="18px" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password Field */}
            <div className="inputGroup">
              <label htmlFor="confirm-password">Confirm Password</label>
              <div className="password-wrapper">
                <span className="input-icon">
                  <PasswordIcon color="rgba(255,255,255,0.6)" width="18px" />
                </span>
                <input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  ref={confirmPasswordRef}
                  required
                  autoComplete="new-password"
                  disabled={isSubmitting}
                  minLength={8}
                />
                <button
                  type="button"
                  className="toggle-visibility"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  disabled={isSubmitting}
                >
                  {showConfirmPassword ? (
                    <EyeIcon color="rgba(255,255,255,0.7)" width="18px" />
                  ) : (
                    <EyeOffIcon color="rgba(255,255,255,0.7)" width="18px" />
                    
                  )}
                </button>
              </div>
            </div>

            {error && (
                <div className={'errorMessage'} role="alert">
                    {error}
                </div>
            )}

            {success && (
                <div className={'successMessage'} role="status">
                    {success}
                </div>
            )}

            {/* Password Requirements Hint */}
            <p className="password-hint">
              Password must be at least 8 characters long.
            </p>

            {/* Submit Button */}
            <button
              type="button"
              className="submit-btn"
              onClick={(e) => handleResetPassword(e)
                // showAlertDialog(
                //   "Confirm Password Reset",
                //   "Are you sure you want to reset your password?",
                //   () => handleResetPassword(e),
                //   undefined
                // )
              }
              disabled={isSubmitting}
            >
              {isSubmitting ? "Resetting..." : "Reset Password"}
            </button>
          </form>

          {/* Back to Login */}
          <button
            type="button"
            className="back-to-login"
            onClick={() => navigate("/login")}
            disabled={isSubmitting}
          >
            <BackIcon color="rgba(255,255,255,0.8)" width="16px" />
            <span>Back to Login</span>
          </button>
        </div>

        {/* Theme Toggle */}
        <button
          className="theme-toggle"
          onClick={() => handleThemeChangeClick(theme === "dark" ? "light" : "dark")}
          aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
        >
          {theme === "dark" ? (
            <LightModeIcon color="white" width="20px" />
          ) : (
            <DarkModeIcon color="white" width="20px" />
          )}
        </button>
      </div>

      {/* Alert Dialog */}
      {alertDialogState && (
        <AlertDialog
          title={alertTitle}
          description={alertDescription}
          windowState={alertDialogState}
          setWindowState={setAlertDialogState}
          function={alertYesCallback}
          okFunction={alertOkCallback}
        />
      )}
    </div>
  );
};

export default ResetForgotPasswordNew2;